import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, fmtN } from "@/components/primitives";
import EmptyStateCard from "@/components/EmptyStateCard";

const OWN_LABEL = { rented: "rented", financed: "financed", owned: "owned", unknown: "unknown", contracted: "contracted", sold: "sold" };

// The two working lists that never existed before this phase:
//  - debt   : contracted machines placed, no beans → a debt conversation
//  - winback: owned/unknown machines placed, no beans → a sales conversation
const OWN_SEGMENTS = [
  ["all", "All"],
  ["debt", "Contracted · debt"],
  ["winback", "Owned / unknown · win-back"],
];
const STATUS_SEGMENTS = [
  ["all", "All"],
  ["stopped", "Stopped ordering"],
  ["never", "Never ordered"],
  ["unknown", "Unknown · no history"],
];
const STATUS_LABEL = {
  stopped: "stopped ordering",
  never: "never ordered",
  unknown: "no orders in available history",
};
const WINDOWS = [[3, "3 mo"], [6, "6 mo"], [12, "12 mo"]];

function Seg({ segs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 2, background: "var(--ui-bg)", padding: 4, borderRadius: 10 }}>
      {segs.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} className="prose-i"
          style={{ padding: "7px 13px", borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer", whiteSpace: "nowrap",
            background: String(active) === String(k) ? "var(--ac-indigo)" : "transparent",
            color: String(active) === String(k) ? "var(--ui-on-accent)" : "var(--ui-text-mid)" }}>{label}</button>
      ))}
    </div>
  );
}

export default function Sites() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("filter") || "dry";
  const ownership = sp.get("ownership") || "all";
  const status = sp.get("status") || "all";
  const window = parseInt(sp.get("window") || "12", 10);

  const setParam = (k, v) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n); };

  const { data, isLoading } = useQuery({
    queryKey: ["sites", filter, ownership, status, window],
    queryFn: () => endpoints.sites({ filter, ownership, status, window }),
  });
  const sites = data?.sites || [];

  const title = filter === "dry" ? "Dry machines — placed, no beans" : "Sites";
  const sub = filter === "dry"
    ? `A machine is on site but no beans have been ordered in the last ${window} months. Contracted sites owe C4B volume (debt); owned or unknown sites are a win-back.`
    : "All sites";

  return (
    <div>
      <div className="flex" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="num" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.9px", color: "var(--ui-text-hi)" }}>{title}</div>
          <div className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-lo)", marginTop: 6, maxWidth: 640 }}>{sub}</div>
        </div>
        <Button variant="secondary" onClick={() => nav("/health")}>← Data Health</Button>
      </div>

      <Card style={{ padding: "18px 22px", marginBottom: 20 }}>
        <div className="flex" style={{ gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginBottom: 6 }}>Ownership</div>
            <Seg segs={OWN_SEGMENTS} active={ownership} onChange={(v) => setParam("ownership", v)} />
          </div>
          <div>
            <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginBottom: 6 }}>Order history</div>
            <Seg segs={STATUS_SEGMENTS} active={status} onChange={(v) => setParam("status", v)} />
          </div>
          <div>
            <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", marginBottom: 6 }}>Zero-spend window</div>
            <Seg segs={WINDOWS} active={window} onChange={(v) => setParam("window", v)} />
          </div>
        </div>
        {data && (
          <div className="flex" style={{ gap: 20, marginTop: 16, flexWrap: "wrap" }}>
            <Stat label="Sites" value={fmtN(data.count)} />
            <Stat label="Contracted · debt" value={fmtN(data.debtCount)} color="var(--sv-crit)" />
            <Stat label="Owned / unknown · win-back" value={fmtN(data.winBackCount)} color="var(--ac-indigo-hi)" />
            <Stat label="Stopped ordering" value={fmtN(data.stoppedCount)} />
            <Stat label="Never ordered" value={fmtN(data.neverCount)} />
            <Stat label="Unknown · no history" value={fmtN(data.unknownCount)} color="var(--sv-watch)" />
          </div>
        )}
        {data?.syncBoundary && (
          <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 14, lineHeight: 1.5 }}>
            Synced history begins {data.syncBoundary}. “Never ordered” is only claimed where the machine was installed after that date; where a machine predates it, the site is “Unknown — no history” rather than assumed never. A longer backfill would move sites out of Unknown.
          </div>
        )}
      </Card>

      <Card style={{ padding: 0 }}>
        {isLoading && <div className="prose-i" style={{ padding: 24, color: "var(--ui-text-lo)" }}>Loading…</div>}
        {!isLoading && sites.length === 0 && (
          <EmptyStateCard testid="sites-empty" message="No sites match this filter." />
        )}
        {sites.map((s, i) => (
          <div key={s.siteId} className="flex items-center" data-testid={`site-row-${s.siteId}`}
            style={{ gap: 12, padding: "15px 24px", borderTop: i === 0 ? "none" : "1px solid var(--ui-line-soft)" }}>
            <div style={{ flex: "1 1 260px", minWidth: 220 }}>
              <div className="prose-i" style={{ fontSize: 14, fontWeight: 600, color: "var(--ui-text-hi)" }}>{s.siteName || "—"}</div>
              <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 2 }}>{s.customerName}</div>
            </div>
            <div style={{ flex: "0 0 150px" }}>
              <span className="prose-i" style={{ fontSize: 11, borderRadius: 8, padding: "3px 9px",
                border: `1px solid ${s.bucket === "debt" ? "var(--sv-crit)" : "var(--ac-indigo)"}`,
                color: s.bucket === "debt" ? "var(--sv-crit)" : "var(--ac-indigo-hi)",
                background: s.bucket === "debt" ? "var(--sv-crit-bg)" : "var(--ac-indigo-soft)" }}>
                {OWN_LABEL[s.ownership] || s.ownership}
              </span>
            </div>
            <div style={{ flex: "0 0 150px" }}>
              <span className="prose-i" style={{ fontSize: 12, color: s.status === "unknown" ? "var(--sv-watch)" : s.status === "never" ? "var(--ui-text-hi)" : "var(--ui-text-mid)" }}>
                {STATUS_LABEL[s.status] || s.status}
              </span>
              {s.earliestMachineInstall && (
                <div className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)", marginTop: 2 }}>machine since {s.earliestMachineInstall}</div>
              )}
            </div>
            <div style={{ flex: "0 0 110px" }}>
              <span className="num" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{s.machineCount}</span>
              <span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}> machine{s.machineCount === 1 ? "" : "s"}</span>
            </div>
            <div style={{ flex: "0 0 90px" }}>
              {s.daysDry != null
                ? <><span className="num" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>{s.daysDry}</span><span className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}> d</span></>
                : <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>—</span>}
            </div>
            <div style={{ flex: "0 0 96px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => nav(`/sites/${s.siteId}`)} className="prose-i" style={{ fontSize: 11.5, color: "var(--ac-indigo)", background: "none", border: "none", cursor: "pointer" }}>Open site →</button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)" }}>{label}</div>
      <div className="num" style={{ fontSize: 22, fontWeight: 700, color: color || "var(--ui-text-hi)", marginTop: 3 }}>{value}</div>
    </div>
  );
}
